import { Check, Globe2 } from "lucide-react";
import { useEffect, useState } from "react";
import { errorMessage, type S } from "../api/client";
import { useUpdateEmbedKey } from "../api/queries";
import { useIntlShape, useT } from "../i18n/I18nProvider";
import { normalizeOrigin, originProblem } from "../lib/snippets";
import { Button, ChipsInput, useToast } from "./ui";

/** Inline editor of a key's allowed origins, saved with one click. */
export function OriginsEditor({ orgId, embedKey, canManage, compact }: {
  orgId: string;
  embedKey: S["EmbedKey"];
  canManage: boolean;
  compact?: boolean;
}) {
  const [origins, setOrigins] = useState(embedKey.allowed_origins);
  const update = useUpdateEmbedKey(orgId);
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  useEffect(() => setOrigins(embedKey.allowed_origins), [embedKey.allowed_origins]);
  const dirty = origins.join(",") !== embedKey.allowed_origins.join(",");

  return (
    <div className="stack" style={{ gap: 10 }}>
      <ChipsInput
        values={origins}
        onChange={setOrigins}
        placeholder={origins.length ? t("website.origins.addAnother") : t("website.origins.example")}
        normalize={normalizeOrigin}
        validate={(o) => originProblem(o, t)}
      />
      <div className="spread">
        <span className="hint">
          <Globe2 size={12} style={{ verticalAlign: -2 }} />{" "}
          {intl.formatMessage({ id: "website.origins.hint" }, { code: (c) => <code dir="ltr">{c}</code>, example: "https://*.example.com" })}
          {embedKey.mode === "test" && <> {t("website.origins.testHint")}</>}
        </span>
        {canManage && (dirty || !compact) && (
          <Button
            size="sm"
            variant={dirty ? "primary" : "default"}
            disabled={!dirty}
            loading={update.isPending}
            icon={<Check />}
            onClick={() =>
              update.mutate(
                { id: embedKey.id, body: { allowed_origins: origins } },
                {
                  onSuccess: () => toast(t("website.origins.saved")),
                  onError: (e) => toast(errorMessage(e), true),
                },
              )
            }
          >
            {t("website.origins.save")}
          </Button>
        )}
      </div>
    </div>
  );
}
